import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Handshake, CalendarCheck, MagnifyingGlass, House, Baby, Diamond, TShirt, Wrench, Hammer, Desktop, Campfire, Confetti, Car, Package, CowboyHat } from "@phosphor-icons/react";
import api from "@/lib/api";
import ItemCard from "@/components/ItemCard";

const HERO_IMG = "https://images.unsplash.com/photo-1650368237704-bf3d79a8b6b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxsaWZlc3R5bGUlMjBmbGF0bGF5JTIwdmludGFnZSUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NzYyMjUyMzZ8MA&ixlib=rb-4.1.0&q=85";
const COMMUNITY_IMG = "https://images.unsplash.com/photo-1772724316741-d71af45b5558?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxjb21tdW5pdHklMjBzaGFyaW5nJTIwZnJpZW5kc3xlbnwwfHx8fDE3NzYyMjUyMzZ8MA&ixlib=rb-4.1.0&q=85";

const CATEGORY_ICONS = {
  household: House, baby: Baby, jewelry: Diamond, cowboy: CowboyHat,
  fashion: TShirt, appliances: Wrench, tools: Hammer, electronics: Desktop,
  outdoor: Campfire, party: Confetti, vehicles: Car, other: Package,
};

const CATEGORY_IMGS = {
  fashion: "https://images.unsplash.com/photo-1758959791346-66e1e8ab21ea?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  tools: "https://images.unsplash.com/photo-1593307315564-c96172dc89dc?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  baby: "https://images.unsplash.com/photo-1622403718261-bd0e7dd01216?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
  electronics: "https://images.unsplash.com/photo-1774735688985-7bbe7ef544c4?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
};

const STEPS = [
  { icon: MagnifyingGlass, title: "Find What You Need", desc: "Browse thousands of items available in your community." },
  { icon: CalendarCheck, title: "Book & Pay Securely", desc: "Reserve items with our secure booking and payment system." },
  { icon: Handshake, title: "Pick Up & Enjoy", desc: "Meet the owner, pick up your rental, and enjoy." },
];

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/items?limit=8&sort=newest").then(r => setFeatured(r.data.items || [])).catch(() => {});
    api.get("/categories").then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  return (
    <main className="bg-[#FDFBF7]">
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover animate-scale-in" />
          <div className="hero-gradient absolute inset-0" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-xl">
            <p className="overline text-[#D96C4A] mb-4 animate-fade-in-up opacity-0 stagger-1" data-testid="hero-overline">The Sharing Economy</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-medium text-[#1A1C1A] tracking-tighter leading-none mb-6 animate-fade-in-up opacity-0 stagger-2" data-testid="hero-heading">
              Rent anything from your neighbors
            </h1>
            <p className="text-base sm:text-lg text-[#5C665D] leading-relaxed mb-8 max-w-md animate-fade-in-up opacity-0 stagger-3">
              From cowboy hats to power drills, designer clothes to baby gear. Why buy when you can borrow?
            </p>
            <div className="flex flex-wrap gap-3 animate-fade-in-up opacity-0 stagger-4">
              <Link to="/browse" className="bg-[#2C5545] text-white hover:bg-[#224235] rounded-full px-8 py-3.5 font-medium transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 inline-flex items-center gap-2" data-testid="hero-browse-btn">
                Start Browsing <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/register" className="bg-white text-[#1A1C1A] border border-[#EAE7E0] hover:border-[#2C5545] rounded-full px-8 py-3.5 font-medium transition-all duration-300 hover:bg-white" data-testid="hero-register-btn">
                List Your Items
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="overline text-[#D96C4A] mb-3">Categories</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1C1A] tracking-tight mb-10">
            Everything you need, one click away
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat.id] || Package;
              const img = CATEGORY_IMGS[cat.id];
              return (
                <Link
                  key={cat.id} to={`/browse?category=${cat.id}`}
                  className="group relative bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-[#2C5545]/20"
                  data-testid={`category-${cat.id}`}
                >
                  {img ? (
                    <div className="aspect-square overflow-hidden">
                      <img src={img} alt={cat.name} className="w-full h-full object-cover img-zoom" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1C1A]/60 to-transparent" />
                      <p className="absolute bottom-3 left-3 right-3 text-white text-sm font-medium">{cat.name}</p>
                    </div>
                  ) : (
                    <div className="aspect-square flex flex-col items-center justify-center p-4 gap-3">
                      <Icon size={32} weight="duotone" className="text-[#2C5545]" />
                      <p className="text-sm font-medium text-[#1A1C1A] text-center">{cat.name}</p>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 sm:py-28 bg-white border-y border-[#EAE7E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="overline text-[#D96C4A] mb-3">How It Works</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1C1A] tracking-tight">
              Three simple steps
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {STEPS.map((step, i) => (
              <div key={i} className="text-center p-8">
                <div className="w-16 h-16 rounded-2xl bg-[#2C5545]/10 flex items-center justify-center mx-auto mb-6">
                  <step.icon size={28} weight="duotone" className="text-[#2C5545]" />
                </div>
                <h3 className="font-heading text-xl font-medium text-[#1A1C1A] mb-3">{step.title}</h3>
                <p className="text-sm text-[#5C665D] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Items */}
      {featured.length > 0 && (
        <section className="py-20 sm:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="overline text-[#D96C4A] mb-3">Just Listed</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1C1A] tracking-tight">
                  Popular right now
                </h2>
              </div>
              <Link to="/browse" className="hidden sm:flex items-center gap-2 text-sm font-medium text-[#2C5545] hover:text-[#224235] transition-colors" data-testid="view-all-items">
                View All <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {featured.slice(0, 8).map(item => <ItemCard key={item.id} item={item} />)}
            </div>
          </div>
        </section>
      )}

      {/* Trust Section */}
      <section className="py-20 sm:py-28 bg-[#2C5545]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="overline text-[#D96C4A] mb-4">Built on Trust</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-medium text-white tracking-tight mb-6">
                A community you can count on
              </h2>
              <p className="text-[#FDFBF7]/70 leading-relaxed mb-8">
                Every member is verified. Every transaction is protected. Borrow connects you with real people in your neighborhood who have exactly what you need.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: ShieldCheck, label: "Verified Users" },
                  { icon: Handshake, label: "Secure Payments" },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <f.icon size={20} weight="duotone" className="text-[#D96C4A]" />
                    </div>
                    <span className="text-sm font-medium text-white">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img src={COMMUNITY_IMG} alt="Community" className="w-full h-80 object-cover animate-scale-in" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1C1A] tracking-tight mb-6">
            Ready to start borrowing?
          </h2>
          <p className="text-[#5C665D] leading-relaxed mb-8 max-w-lg mx-auto">
            Join thousands of people who are saving money and reducing waste by sharing instead of buying.
          </p>
          <Link to="/register" className="bg-[#D96C4A] text-white hover:bg-[#C25939] rounded-full px-10 py-4 font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 inline-flex items-center gap-2" data-testid="cta-register-btn">
            Get Started Free <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#EAE7E0] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-heading text-xl font-semibold text-[#2C5545]">Borrow</span>
          <p className="text-xs text-[#5C665D]">Share more. Live better.</p>
        </div>
      </footer>
    </main>
  );
}
